<template>
  <LoadingBlock :loading="loading">
    <form @submit.prevent="submit">
      <input type="hidden" v-model="item_data.id">
      <input type="hidden" v-model="item_data.company_id">

      <div class="col mb-4">
        <label class="form-label">Вид</label>
        <Select v-model="item_data.kind" :options="kindOptions" optionLabel="name" optionValue="code"
                style="width: 100%" required/>
      </div>

      <div class="col mb-4">
        <label class="form-label">Номер</label>
        <input type="text" class="form-control" required v-model="item_data.number">
      </div>

      <div class="col mb-4">
        <label class="form-label">Тип соответствия</label>
        <Select v-model="item_data.compliance_type" :options="complianceOptions" optionLabel="name"
                optionValue="code" style="width: 100%" required/>
      </div>

      <div class="col mb-4">
        <label class="form-label">Дата выдачи</label>
        <DatePicker v-model="issueDate" dateFormat="dd.mm.yy" style="width: 100%" required/>
      </div>

      <div class="col mb-4">
        <div class="form-check">
          <input class="form-check-input" type="checkbox" id="is-perpetual" v-model="item_data.is_perpetual"
                 @change="onPerpetualChange">
          <label class="form-check-label" for="is-perpetual">Бессрочный</label>
        </div>
      </div>

      <div v-if="!item_data.is_perpetual" class="col mb-4">
        <label class="form-label">Срок действия</label>
        <DatePicker v-model="validUntilDate" dateFormat="dd.mm.yy" style="width: 100%"/>
      </div>

      <div class="col mb-4">
        <label class="form-label">Бренды</label>
        <Chips v-model="item_data.brands" style="width: 100%"/>
      </div>

      <div class="col mb-4">
        <label class="form-label">Артикулы</label>
        <Chips v-model="item_data.articles" style="width: 100%"/>
      </div>

      <div class="col mb-4">
        <label class="form-label">ТНВЭД</label>
        <Chips v-model="item_data.tnved" style="width: 100%"/>
      </div>

      <LoadingBlock :loading="documentLoading">
        <div class="col mb-4">
          <label class="form-label">Документ</label>
          <input ref="documentInput" type="file" class="form-control" accept=".pdf,.jpg,.jpeg,.png"
                 @change="onDocumentChange">
          <div v-if="item_data.document?.file_name" class="mt-2 text-muted">
            Текущий файл: {{ item_data.document.file_name }}
          </div>
          <div v-else-if="item_data.registry_urls?.length" class="mt-2 text-muted">
            Файла нет, документ из реестра:
            <a :href="item_data.registry_urls[0]" target="_blank" rel="noopener noreferrer">открыть</a>
          </div>
        </div>
      </LoadingBlock>

      <div v-if="draftRestored" class="col mb-4 text-muted small text-center">
        Восстановлен несохранённый черновик
      </div>

      <div class="col mb-4 text-center">
        <button type="submit" class="btn btn-primary me-4 waves-effect waves-light">Сохранить</button>
        <button v-if="!item_data.id" type="button" class="btn btn-outline-secondary waves-effect"
                @click="resetDraft">Очистить
        </button>
      </div>
    </form>
  </LoadingBlock>
</template>

<script>
import LoadingBlock from "@/components/page/LoadingBlock.vue";
import {Message, Messages} from "@/data/messages";
import {createItem, updateItem, uploadDocument} from "@/api/certificate";
import {user} from "@/data/user";

const DRAFT_KEY_PREFIX = 'certificate_draft_'

function draftStorageKey() {
    return DRAFT_KEY_PREFIX + (user.active_company || localStorage.getItem('active_company') || '')
}

function readDraft() {
    try {
        const raw = localStorage.getItem(draftStorageKey())
        return raw ? JSON.parse(raw) : null
    } catch (e) {
        return null
    }
}

function writeDraft(draft) {
    try {
        localStorage.setItem(draftStorageKey(), JSON.stringify(draft))
    } catch (e) {
        // хранилище недоступно — черновик живёт только в памяти
    }
}

function removeDraft() {
    try {
        localStorage.removeItem(draftStorageKey())
    } catch (e) {
        // ignore
    }
}
import {Chips, DatePicker, Select} from "primevue";
import {
    certificateKinds,
    complianceTypes,
    isAllowedCertificateFile,
    parseCertificateError,
    parseDateForPicker,
    toDateString,
    validateDates,
} from "@/data/certificate";

const emptyItem = () => ({
    id: "",
    company_id: "",
    kind: "",
    number: "",
    compliance_type: "",
    issue_date: "",
    valid_until: null,
    is_perpetual: false,
    document: null,
    brands: [],
    articles: [],
    tnved: [],
});

export default {
  name: "CertificateForm",
  components: {LoadingBlock, Select, DatePicker, Chips},
  data() {
    return {
      item_data: emptyItem(),
      kindOptions: certificateKinds,
      complianceOptions: complianceTypes,
      loading: false,
      documentLoading: false,
      pendingDocumentFile: null,
      issueDate: null,
      validUntilDate: null,
      draftRestored: false,
    }
  },
  computed: {
    draftSnapshot() {
      return {
        item_data: this.item_data,
        issue_date: toDateString(this.issueDate),
        valid_until: toDateString(this.validUntilDate),
      }
    },
  },
  watch: {
    draftSnapshot: {
      deep: true,
      handler(snapshot) {
        // черновик ведём только для нового сертификата
        if (snapshot.item_data.id) {
          return
        }
        if (!this.hasDraftData(snapshot)) {
          removeDraft()
        } else {
          writeDraft(snapshot)
        }
      },
    },
  },
  methods: {
    hasDraftData(snapshot) {
      const d = snapshot.item_data
      return Boolean(d.kind || d.number || d.compliance_type || d.is_perpetual || d.document
          || d.brands?.length || d.articles?.length || d.tnved?.length
          || snapshot.issue_date || snapshot.valid_until)
    },
    clearDocumentInput() {
      this.pendingDocumentFile = null
      if (this.$refs.documentInput) {
        this.$refs.documentInput.value = ''
      }
    },
    startNew() {
      // Если форма уже содержит несохранённый новый сертификат — оставляем как есть
      if (!this.item_data.id && (this.hasDraftData(this.draftSnapshot) || this.pendingDocumentFile)) {
        return
      }
      const draft = readDraft()
      this.clearDocumentInput()
      if (draft?.item_data) {
        this.item_data = {
          ...emptyItem(),
          ...draft.item_data,
          id: "",
          brands: draft.item_data.brands || [],
          articles: draft.item_data.articles || [],
          tnved: draft.item_data.tnved || [],
        }
        this.issueDate = parseDateForPicker(draft.issue_date)
        this.validUntilDate = parseDateForPicker(draft.valid_until)
        this.draftRestored = true
      } else {
        this.item_data = emptyItem()
        this.issueDate = null
        this.validUntilDate = null
        this.draftRestored = false
      }
    },
    resetDraft() {
      removeDraft()
      this.clearDocumentInput()
      this.item_data = emptyItem()
      this.issueDate = null
      this.validUntilDate = null
      this.draftRestored = false
    },
    onPerpetualChange() {
      if (this.item_data.is_perpetual) {
        this.validUntilDate = null
        this.item_data.valid_until = null
      }
    },
    onDocumentChange(event) {
      const file = event.target.files?.[0]
      if (!file) {
        return
      }
      if (!isAllowedCertificateFile(file)) {
        const message = new Message()
        message.type = 'failure'
        message.text = 'Недопустимый тип файла. Допустимы PDF, JPEG, PNG'
        Messages.list.push(message)
        event.target.value = ''
        return
      }
      this.pendingDocumentFile = file
    },
    buildPayload() {
      const data = {...this.item_data}
      data.issue_date = toDateString(this.issueDate)
      data.valid_until = this.item_data.is_perpetual ? null : toDateString(this.validUntilDate)
      data.brands = data.brands?.length ? data.brands : null
      data.articles = data.articles?.length ? data.articles : null
      data.tnved = data.tnved?.length ? data.tnved : null
      return data
    },
    async uploadPendingDocument() {
      if (!this.pendingDocumentFile) {
        return this.item_data.document
      }
      this.documentLoading = true
      try {
        const result = await uploadDocument(this.pendingDocumentFile)
        this.pendingDocumentFile = null
        return result.data
      } finally {
        this.documentLoading = false
      }
    },
    async submit() {
      const payload = this.buildPayload()
      const dateError = validateDates(payload)
      if (dateError) {
        const message = new Message()
        message.type = 'failure'
        message.text = dateError
        Messages.list.push(message)
        return
      }

      this.loading = true
      const message = new Message()
      try {
        const document = await this.uploadPendingDocument()
        if (document) {
          payload.document = document
        }

        if (this.item_data.id) {
          payload.id = this.item_data.id
          const result = await updateItem(this.item_data.id, payload)
          if (result.status === 200 || result.status === 201) {
            message.type = 'success'
            message.autoremove = 10
            message.text = 'Сертификат успешно обновлён'
            this.$emit('success', result.data)
          } else {
            message.type = 'failure'
            message.text = 'Не удалось обновить сертификат'
          }
        } else {
          payload.company_id = await user.getActiveCompany()
          const result = await createItem(payload)
          if (result.status === 200 || result.status === 201) {
            message.type = 'success'
            message.autoremove = 10
            message.text = 'Сертификат успешно создан'
            this.resetDraft()
            this.$emit('success', result.data)
          } else {
            message.type = 'failure'
            message.text = 'Не удалось создать сертификат'
          }
        }
      } catch (e) {
        message.type = 'failure'
        message.text = parseCertificateError(e)
      }
      Messages.list.push(message)
      this.loading = false
    },
    updateItem(item) {
      this.clearDocumentInput()
      this.draftRestored = false
      this.item_data = {
        ...emptyItem(),
        ...item,
        brands: item.brands || [],
        articles: item.articles || [],
        tnved: item.tnved || [],
      }
      this.issueDate = parseDateForPicker(item.issue_date)
      this.validUntilDate = item.is_perpetual ? null : parseDateForPicker(item.valid_until)
    },
  },
}
</script>
